"use client";

import { useMemo } from "react";
import { useRouter } from "next/navigation";
import {
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { CHART_COLORS, TOOLTIP_STYLE } from "@/lib/recharts-theme";
import { formatCompactNumber, formatMoney } from "@/lib/format";

export type MapPoint = {
  name: string;
  slug: string;
  startupCount: number;
  marketDemand: number;
  totalMrr: number;
  opportunityScore: number;
};

type Props = {
  data: MapPoint[];
  medianStartups: number;
  medianDemand: number;
};

const LOG_DOMAIN_MIN = 1;

function QuadrantLabels() {
  return (
    <g>
      <text
        x="15%"
        y="15%"
        textAnchor="middle"
        fill={CHART_COLORS.goldmine}
        fontSize={13}
        fontWeight={700}
        opacity={0.9}
      >
        Goldmine
      </text>
      <text
        x="85%"
        y="15%"
        textAnchor="middle"
        fill={CHART_COLORS.competitive}
        fontSize={13}
        fontWeight={700}
        opacity={0.9}
      >
        Competitive
      </text>
      <text
        x="15%"
        y="82%"
        textAnchor="middle"
        fill={CHART_COLORS.premium}
        fontSize={13}
        fontWeight={700}
        opacity={0.9}
      >
        Premium
      </text>
      <text
        x="85%"
        y="82%"
        textAnchor="middle"
        fill={CHART_COLORS.danger}
        fontSize={13}
        fontWeight={700}
        opacity={0.9}
      >
        Dead
      </text>
    </g>
  );
}

export function MarketMapChart({ data, medianStartups, medianDemand }: Props) {
  const router = useRouter();

  const { xMax, yMax, filteredData } = useMemo(() => {
    const xValues = data.map((d) => d.startupCount).filter((v) => v >= LOG_DOMAIN_MIN);
    const yValues = data.map((d) => d.marketDemand).filter((v) => v >= LOG_DOMAIN_MIN);
    const xMaxVal = xValues.length > 0 ? Math.max(...xValues) * 1.05 : 100;
    const dataYMax = yValues.length > 0 ? Math.max(...yValues) * 1.05 : 100000;
    const symmetricYMax = medianDemand >= LOG_DOMAIN_MIN ? medianDemand * medianDemand : dataYMax;
    const yMaxVal = Math.max(dataYMax, symmetricYMax);
    const filtered = data.filter(
      (d) => d.startupCount >= LOG_DOMAIN_MIN && d.marketDemand >= LOG_DOMAIN_MIN
    );
    return {
      xMax: Math.max(xMaxVal, 10),
      yMax: Math.max(yMaxVal, 1000),
      filteredData: filtered,
    };
  }, [data, medianDemand]);

  const safeMedianStartups = Math.max(medianStartups, LOG_DOMAIN_MIN);
  const safeMedianDemand = Math.max(medianDemand, LOG_DOMAIN_MIN);

  if (data.length === 0) {
    return (
      <div className="chart-placeholder" style={{ height: 400 }}>
        No niche data to display
      </div>
    );
  }

  return (
    <div className="chart-container" style={{ height: 420, minHeight: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 24, right: 24, bottom: 24, left: 24 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4} />

          {/* Colored quadrant backgrounds - render first so points appear on top */}
          <ReferenceArea
            x1={LOG_DOMAIN_MIN}
            x2={safeMedianStartups}
            y1={safeMedianDemand}
            y2={yMax}
            fill={CHART_COLORS.goldmine}
            fillOpacity={0.18}
            stroke="none"
            zIndex={0}
          />
          <ReferenceArea
            x1={safeMedianStartups}
            x2={xMax}
            y1={safeMedianDemand}
            y2={yMax}
            fill={CHART_COLORS.competitive}
            fillOpacity={0.18}
            stroke="none"
            zIndex={0}
          />
          <ReferenceArea
            x1={LOG_DOMAIN_MIN}
            x2={safeMedianStartups}
            y1={LOG_DOMAIN_MIN}
            y2={safeMedianDemand}
            fill={CHART_COLORS.premium}
            fillOpacity={0.18}
            stroke="none"
            zIndex={0}
          />
          <ReferenceArea
            x1={safeMedianStartups}
            x2={xMax}
            y1={LOG_DOMAIN_MIN}
            y2={safeMedianDemand}
            fill={CHART_COLORS.danger}
            fillOpacity={0.18}
            stroke="none"
            zIndex={0}
          />

          <XAxis
            type="number"
            dataKey="startupCount"
            name="Startups"
            scale="log"
            domain={[LOG_DOMAIN_MIN, xMax]}
            tick={{ fontSize: 11, fill: "var(--text-muted)" }}
            tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v))}
            label={{ value: "Startups (log)", position: "bottom", fontSize: 12 }}
          />
          <YAxis
            type="number"
            dataKey="marketDemand"
            name="Market Demand"
            scale="log"
            domain={[LOG_DOMAIN_MIN, yMax]}
            tick={{ fontSize: 11, fill: "var(--text-muted)" }}
            tickFormatter={(v) => formatCompactNumber(v)}
            label={{ value: "Market Demand (log)", angle: -90, position: "insideLeft", fontSize: 12 }}
          />
          <ZAxis type="number" dataKey="totalMrr" range={[36, 180]} />

          <ReferenceLine
            x={safeMedianStartups}
            stroke="var(--text-muted)"
            strokeWidth={1.5}
            strokeDasharray="5 5"
            opacity={0.7}
            zIndex={10}
          />
          <ReferenceLine
            y={safeMedianDemand}
            stroke="var(--text-muted)"
            strokeWidth={1.5}
            strokeDasharray="5 5"
            opacity={0.7}
            zIndex={10}
          />

          <Tooltip
            {...TOOLTIP_STYLE}
            cursor={{ strokeDasharray: "3 3", stroke: "var(--accent)" }}
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null;
              const p = payload[0].payload as MapPoint;
              return (
                <div
                  style={{
                    ...TOOLTIP_STYLE.contentStyle,
                    padding: "12px 14px",
                  }}
                >
                  <div style={{ ...TOOLTIP_STYLE.labelStyle, marginBottom: 6 }}>{p.name}</div>
                  <div style={{ fontSize: "0.85em", color: "var(--text-secondary)" }}>
                    Startups: {p.startupCount}
                  </div>
                  <div style={{ fontSize: "0.85em", color: "var(--text-secondary)" }}>
                    Demand: {formatCompactNumber(p.marketDemand)}
                  </div>
                  <div style={{ fontSize: "0.85em", color: "var(--text-secondary)" }}>
                    Total MRR: {formatMoney(p.totalMrr)}
                  </div>
                  <div style={{ fontSize: "0.85em", color: "var(--text-secondary)" }}>
                    Score: {p.opportunityScore}
                  </div>
                </div>
              );
            }}
          />

          <QuadrantLabels />

          <Scatter
            data={filteredData}
            fill="var(--text-primary)"
            fillOpacity={0.9}
            stroke="var(--bg-surface)"
            strokeWidth={2}
            onClick={(item) => {
              const payload = item?.payload as MapPoint | undefined;
              if (payload) router.push(`/dashboard/${payload.slug}`);
            }}
            cursor="pointer"
            zIndex={20}
          />
        </ScatterChart>
      </ResponsiveContainer>
      {filteredData.length < data.length && (
        <p
          style={{
            marginTop: "0.5rem",
            fontSize: "0.75rem",
            color: "var(--text-muted)",
          }}
        >
          Showing {filteredData.length} of {data.length} niches (log scale; values &lt; 1 excluded)
        </p>
      )}
    </div>
  );
}
